import { BooleanField, FormType, TextField } from "@hex-insights/forms";
import { UserGroupFormValues } from "../../FormUtilities";
import { ClassYearSelectField } from "../ClassYear";
import { ClassYearCategorySelectField } from "../ClassYearCategory";
import { CourseSectionSelectField } from "../CourseSection";
import { HomeRoomSectionSelectField } from "../HomeRoomSection";
import { BaseFieldProps } from "../shared";
import { UserSelectField } from "../User";

/**
 * Generic props for fields of the UserGroup model.
 */
type FieldProps<K extends keyof UserGroupFormValues.Base = keyof UserGroupFormValues.Base> = BaseFieldProps<
	Pick<UserGroupFormValues.Base, K>
>;

/**
 * Field component for the `name` field of the UserGroup model.
 */
export function Name({ formState, ...props }: FieldProps<"name">) {
	return <TextField formState={formState} name="name" {...props} />;
}

/**
 * Field component for the `description` field of the UserGroup model.
 */
export function Description({ formState, ...props }: FieldProps<"description">) {
	return <TextField formState={formState} name="description" multiLine optional {...props} />;
}

/**
 * Field component for the `color` field of the UserGroup model.
 */
export function Color({ formState, ...props }: FieldProps<"color">) {
	return <TextField formState={formState} name="color" optional={FormType.isCreate(formState.formType)} {...props} />;
}

/**
 * Field component for the `isPublic` field of the UserGroup model.
 */
export function IsPublic({ formState, ...props }: FieldProps<"isPublic">) {
	return <BooleanField formState={formState} name="isPublic" {...props} />;
}

/**
 * Field component for the `includeEmployees` field of the UserGroup model.
 */
export function IncludeEmployees({ formState, ...props }: FieldProps<"includeEmployees">) {
	return <BooleanField formState={formState} name="includeEmployees" {...props} />;
}

/**
 * Field component for the `includeGuardians` field of the UserGroup model.
 */
export function IncludeGuardians({ formState, ...props }: FieldProps<"includeGuardians">) {
	return <BooleanField formState={formState} name="includeGuardians" {...props} />;
}

/**
 * Field component for the `includeAllUsersOfPersonTypes` field of the UserGroup model.
 */
export function IncludeAllUsersOfPersonTypes({ formState, ...props }: FieldProps<"includeAllUsersOfPersonTypes">) {
	return <BooleanField formState={formState} name="includeAllUsersOfPersonTypes" {...props} />;
}

export namespace ClassYearCategories {
	export type SelectProps = FieldProps<"classYearCategoryIDs"> &
		Omit<ClassYearCategorySelectField.MultiSelectProps<UserGroupFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `classYearCategories` edge of the UserGroup model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <ClassYearCategorySelectField.MultiSelect formState={formState} name="classYearCategoryIDs" {...props} />;
	}
}

export namespace ClassYears {
	export type SelectProps = FieldProps<"classYearIDs"> &
		Omit<ClassYearSelectField.MultiSelectProps<UserGroupFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `classYears` edge of the UserGroup model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <ClassYearSelectField.MultiSelect formState={formState} name="classYearIDs" {...props} />;
	}
}

export namespace CourseSections {
	export type SelectProps = FieldProps<"courseSectionIDs"> &
		Omit<CourseSectionSelectField.MultiSelectProps<UserGroupFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `courseSections` edge of the UserGroup model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CourseSectionSelectField.MultiSelect formState={formState} name="courseSectionIDs" {...props} />;
	}
}

export namespace HomeRoomSections {
	export type SelectProps = FieldProps<"homeRoomSectionIDs"> &
		Omit<HomeRoomSectionSelectField.MultiSelectProps<UserGroupFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `homeRoomSections` edge of the UserGroup model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <HomeRoomSectionSelectField.MultiSelect formState={formState} name="homeRoomSectionIDs" {...props} />;
	}
}

export namespace Users {
	export type SelectProps = FieldProps<"userIDs"> &
		Omit<UserSelectField.MultiSelectProps<UserGroupFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `users` edge of the UserGroup model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.MultiSelect formState={formState} name="userIDs" label="Additional Users" {...props} />;
	}
}
